import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, Loader2, MapPin, Lock } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FileUpload from "@/components/FileUpload";
import api from "@/lib/api";
import useFormDraft from "@/lib/useFormDraft";
import { useUser } from "@/lib/userContext";

const INITIAL_DRAFT = {
    files: [],
    active_slug: null,
    form: {
        full_name: "",
        email: "",
        phone: "",
        portfolio_url: "",
        linkedin: "",
        screening_answer_1: "",
        screening_answer_2: "",
        screening_answer_3: "",
    },
};

function Field({ label, children, span = "" }) {
    return (
        <div className={`flex flex-col ${span}`}>
            <span className="field-label">{label}</span>
            {children}
        </div>
    );
}

export default function Careers() {
    const { user, signIn } = useUser();
    const [jobs, setJobs] = useState([]);
    const [draft, setDraft, clearDraft] = useFormDraft("draft_careers", INITIAL_DRAFT);
    const { files, form, active_slug } = draft;
    const setForm = (next) => setDraft((d) => ({ ...d, form: typeof next === "function" ? next(d.form) : next }));
    const setFiles = (next) => setDraft((d) => ({ ...d, files: typeof next === "function" ? next(d.files) : next }));
    const setActive = (job) => setDraft((d) => ({ ...d, active_slug: job ? job.slug : null }));
    const active = active_slug ? jobs.find((j) => j.slug === active_slug) : null;

    const [tracking, setTracking] = useState(null);
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();
    const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    useEffect(() => {
        api.get("/jobs").then((r) => setJobs(r.data)).catch(() => toast.error("Could not load roles"));
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!active) return;
        const required = ["full_name", "email", "phone", "screening_answer_1", "screening_answer_2"];
        const missing = required.find((k) => !form[k]?.trim?.());
        if (missing) return toast.error("Please complete all required fields");
        if (!user) {
            toast.message("Sign in to apply", { description: "We'll bring you right back to this form." });
            signIn("/careers");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                ...form,
                role_slug: active.slug,
                role_title: active.title,
                file_paths: files.map((f) => f.id),
            };
            const { data } = await api.post("/applications/careers", payload);
            setTracking(data.tracking_id);
            clearDraft();
            toast.success("Application submitted");
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.detail || "Could not submit");
        } finally {
            setBusy(false);
        }
    };

    if (tracking) {
        return (
            <main className="min-h-screen" data-testid="careers-success">
                <Navbar />
                <section className="pt-40 pb-32">
                    <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 border is-active mb-8">
                            <Check size={20} />
                        </div>
                        <h1 className="font-display text-4xl lg:text-5xl tracking-tighter mb-6">Application received.</h1>
                        <p className="text-white/65 text-lg mb-10 max-w-xl mx-auto">
                            Pipeline: <span className="text-saffron">Applied → Under Review → Assignment → Interview → Final Decision</span>
                        </p>
                        <div className="card-surface p-8 inline-block">
                            <div className="eyebrow mb-2">Your Candidate ID</div>
                            <div className="font-mono text-2xl text-saffron tracking-widest" data-testid="careers-tracking-id">
                                {tracking}
                            </div>
                        </div>
                        <div className="mt-12 flex flex-wrap justify-center gap-3">
                            <button onClick={() => navigate(`/status/${tracking}`)} className="btn-primary" data-testid="careers-track-btn">
                                Track Application <ArrowUpRight size={16} />
                            </button>
                            <button onClick={() => navigate("/")} className="btn-secondary">Back to Home</button>
                        </div>
                    </div>
                </section>
                <Footer />
            </main>
        );
    }

    return (
        <main className="min-h-screen" data-testid="careers-page">
            <Navbar />
            <section className="pt-32 pb-16 lg:pt-44 lg:pb-20 border-b border-white/8">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-5 text-saffron">Careers · Internships & Full-time</div>
                    <h1 className="font-display text-5xl lg:text-7xl tracking-tighter leading-[0.95] mb-6 max-w-3xl">
                        Build your career. <br />
                        Build <span className="text-saffron">with us</span>.
                    </h1>
                    <p className="text-white/65 text-lg max-w-2xl">
                        We hire for craft, curiosity and conviction. If your work makes people stop scrolling, we
                        want to talk.
                    </p>
                </div>
            </section>

            <section className="py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    {!active && (
                        <div className="space-y-3">
                            {jobs.length === 0 && (
                                <div className="card-surface p-12 lg:p-16 text-center">
                                    <div className="eyebrow mb-3">No open roles right now</div>
                                    <h3 className="font-display text-2xl mb-3">Check back soon.</h3>
                                    <p className="text-white/55 text-sm">We post selectively. Follow us for announcements.</p>
                                </div>
                            )}
                            {jobs.map((j) => (
                                <button
                                    key={j.slug}
                                    onClick={() => setActive(j)}
                                    data-testid={`career-role-${j.slug}`}
                                    className="card-surface w-full text-left p-6 lg:p-8 group grid grid-cols-1 lg:grid-cols-12 gap-6 items-center"
                                >
                                    <div className="lg:col-span-8">
                                        <div className="font-display text-2xl mb-2">{j.title}</div>
                                        <div className="text-white/55 text-sm mb-3 line-clamp-2">{j.summary}</div>
                                        <div className="flex flex-wrap gap-2 text-xs text-white/55">
                                            <span className="tag">{j.type}</span>
                                            <span className="tag flex items-center gap-1">
                                                <MapPin size={10} /> {j.location}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="lg:col-span-4 flex lg:justify-end">
                                        <span className="inline-flex items-center justify-center gap-2 text-sm text-white/85 group-hover:text-saffron-brand border border-white/15 group-hover:border-saffron px-5 py-2.5 transition-colors whitespace-nowrap" style={{minWidth: "140px"}}>
                                            Apply <ArrowUpRight size={14} />
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}

                    {active && (
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                            <div className="lg:col-span-4 lg:sticky lg:top-28 self-start space-y-6">
                                <button onClick={() => setActive(null)} className="text-white/55 hover:text-white text-sm" data-testid="career-back-btn">
                                    ← Back to roles
                                </button>
                                <div className="card-surface p-6">
                                    <div className="eyebrow mb-3">Role</div>
                                    <div className="font-display text-2xl mb-3">{active.title}</div>
                                    <div className="flex flex-wrap gap-2 mb-5">
                                        <span className="tag">{active.type}</span>
                                        <span className="tag">{active.location}</span>
                                    </div>
                                    <p className="text-white/55 text-sm leading-relaxed mb-4">{active.summary}</p>
                                    <div className="eyebrow mb-2">Responsibilities</div>
                                    <ul className="space-y-2 text-white/75 text-sm">
                                        {active.responsibilities.map((r) => (
                                            <li key={r} className="flex gap-2">
                                                <span className="text-saffron">›</span>
                                                {r}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <form onSubmit={submit} className="lg:col-span-8 card-surface p-8 lg:p-12 space-y-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                                    <Field label="Full Name">
                                        <input className="field-input" value={form.full_name} onChange={set("full_name")} data-testid="hr-input-full_name" />
                                    </Field>
                                    <Field label="Email">
                                        <input type="email" className="field-input" value={form.email} onChange={set("email")} data-testid="hr-input-email" />
                                    </Field>
                                    <Field label="Phone">
                                        <input className="field-input" value={form.phone} onChange={set("phone")} data-testid="hr-input-phone" />
                                    </Field>
                                    <Field label="LinkedIn">
                                        <input className="field-input" value={form.linkedin} onChange={set("linkedin")} data-testid="hr-input-linkedin" />
                                    </Field>
                                    <Field label="Portfolio URL" span="md:col-span-2">
                                        <input className="field-input" value={form.portfolio_url} onChange={set("portfolio_url")} data-testid="hr-input-portfolio_url" />
                                    </Field>
                                </div>

                                {active.screening_questions.map((q, i) => {
                                    const key = `screening_answer_${i + 1}`;
                                    return (
                                        <Field key={i} label={`Q${i + 1}. ${q}`}>
                                            <textarea
                                                className="field-input"
                                                rows={3}
                                                value={form[key] || ""}
                                                onChange={set(key)}
                                                data-testid={`hr-input-${key}`}
                                            />
                                        </Field>
                                    );
                                })}

                                <Field label="Resume / Portfolio (PDF)">
                                    <FileUpload category="careers" value={files} onChange={setFiles} testid="hr-upload" />
                                </Field>

                                <div className="flex justify-between items-center gap-4 flex-wrap">
                                    {!user && (
                                        <span className="text-xs text-white/55 flex items-center gap-1.5" data-testid="hr-signin-hint">
                                            <Lock size={12} className="text-saffron-brand" /> Sign in required to submit
                                        </span>
                                    )}
                                    <div className="ml-auto">
                                        <button type="submit" disabled={busy} className="btn-primary" data-testid="hr-submit-btn">
                                            {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} />}
                                            {user ? "Submit Application" : "Sign in & Submit"}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </section>
            <Footer />
        </main>
    );
}
